package de.oevbs.kunde.kundenportal2.services.contract

import de.oevbs.businesspartner.bpcrmservice2.common.model.LineOfBusiness
import de.oevbs.businesspartner.bpcrmservice2.contract.model.Contract
import de.oevbs.kosmos.ui.forms.selectmenu.selectMenu
import de.oevbs.kosmos.ui.forms.switch.switchInlined
import de.oevbs.kosmos.ui.icons.MaterialIconsOutlined
import de.oevbs.kosmos.ui.icons.icon
import de.oevbs.kosmos.ui.layouts.card
import de.oevbs.kunde.kundenportal2.Pages
import de.oevbs.kunde.kundenportal2.components.*
import de.oevbs.kunde.kundenportal2.lineOfBusinessToString
import de.oevbs.kunde.kundenportal2.model.services.contract.ContractFilter
import de.oevbs.kunde.kundenportal2.model.services.contract.lineOfBusiness
import de.oevbs.kunde.kundenportal2.model.services.contract.showArchived
import de.oevbs.kunde.kundenportal2.navigation.visit
import de.oevbs.sfa.data.time.toLocalizedString
import dev.fritz2.core.RenderContext
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.map

fun RenderContext.initAndRenderContractList(contractStore: ContractStore) {
    contractStore.loadContracts(Unit)

    globalInitTracker.data.combine(contractStore.initFailedFlow, ::Pair).render { (loading, failed) ->
        if (loading) return@render
        when {
            failed -> noContractDataFoundMessage()
            else -> renderContractList(contractStore)
        }
    }
}

private fun RenderContext.renderContractList(contractStore: ContractStore) {
    paragraph {
        +"In der folgenden Übersicht finden Sie Informationen zu Ihren Verträgen. Klicken Sie einen Vertrag an, um Details und Service-Optionen zu sehen."
        br {}
        br {}
        +" Wenn Sie für Ihre Steuererklärung eine Bescheinigung über den gezahlten Beitrag zu Ihren Versicherungen brauchen,"
        b { +" nutzen Sie einfach Ihre jährliche Beitragsrechnung oder Ihren Versicherungsschein als Nachweis." }
        +" Diese Dokumente finden Sie in Ihrem "
        textualButton("Posteingang ") handledBy { Pages.inboxInbox.visit() }
        +". Für Kfz-Versicherungen können Sie darüber hinaus in der Detailansicht Ihres Vertrages eine Steuerbescheinigung anfordern."
    }

    renderFilter()
    contractStore.data.renderIf({ it.isEmpty() }) {
        paragraph {
            +"Es wurden keine Verträge gefunden."
        }
    }
    contractStore.filteredAndSequenced.renderEach { contract ->
        renderContract(contract, contractStore)
    }
}

private fun RenderContext.renderContract(contract: Contract, contractStore: ContractStore) = card {
    div("cursor-pointer") {
        contractTitle(contract)
        detailsGrid {
            contractOverviewGeneralAspects(contract)
            when (LineOfBusiness.get(contract.lineOfBusiness.key, contract.externalSystem)) {
                LineOfBusiness.Kfz -> contractOverviewCar(contract)
                LineOfBusiness.Sach -> contractOverviewProperty(contract)
                LineOfBusiness.SachLegacy -> contractOverviewPropertyLegacy(contract)
                LineOfBusiness.Unfall -> contractOverviewAccident(contract)
                LineOfBusiness.Leben -> contractOverviewLife(contract)
                else -> {}
            }
        }
        div("flex w-full justify-end") {
            icon("h-10 w-10 text-gray-600", icon = MaterialIconsOutlined.arrow_circle_right)
        }
    }
    clicks.map { contract.contractNumber } handledBy contractStore.showDetails
}

private fun RenderContext.renderFilter() =
    div("flex flex-col md:flex-row gap-2 md:gap-6 md:justify-end md:items-center") {
        selectMenu<LineOfBusiness> {
            value(FilterStore.map(ContractFilter.lineOfBusiness()))
            options = LineOfBusiness.all
            optionsFormat = ::lineOfBusinessToString
        }
        switchInlined {
            label("Inaktive Verträge anzeigen")
            value(FilterStore.map(ContractFilter.showArchived()))
        }
    }

/**
 * Allgemeine Vertragsinformationen
 *
 * @param contract beinhaltet die Vertragsinformationen
 */
private fun RenderContext.contractOverviewGeneralAspects(contract: Contract) {
    val product = when (contract.lineOfBusiness) {
        LineOfBusiness.Haftpflicht -> contract.additionalLine1.ifEmpty { contract.branchGroup }
        else -> contract.branchGroup
    }
    detailsGridEntry("Produkt", product)
    detailsGridEntry("Beitrag", "${contract.rate.toEuro()} ${contract.paymentSchedule.value}")
    detailsGridEntry("Beginn", contract.begin?.toLocalizedString())
    detailsGridEntry("Ablauf", contract.end?.toLocalizedString())
    detailsGridEntry("Status", contract.status.value)
}

/**
 * Anzeige von Kfz Informationen
 *
 * @param contract beinhaltet die Vertragsinformationen
 */
private fun RenderContext.contractOverviewCar(contract: Contract) {
    detailsGridEntry("Kennzeichen", contract.additionalLine2)
    detailsGridEntry("Info", contract.additionalLine1)
}

/**
 * Anzeige von Sach - Gebäude Informationen
 *
 * @param contract beinhaltet die Vertragsinformationen
 */
private fun RenderContext.contractOverviewProperty(contract: Contract) {
    if (contract.additionalLine2.isNotEmpty()) {
        detailsGridEntry("Versicherungsort", contract.additionalLine2)
    }
}

/**
 * Anzeige von Sach - Hausrat/Haushaltglas Gebäude (Alt) Informationen
 *
 * @param contract beinhaltet die Vertragsinformationen
 */
private fun RenderContext.contractOverviewPropertyLegacy(contract: Contract) {
    if (contract.additionalLine3.isNotEmpty()) {
        detailsGridEntry("Versicherungsort", contract.additionalLine3)
    }
}

/**
 * Anzeige von Unfall Informationen
 *
 * @param contract beinhaltet die Vertragsinformationen
 */
private fun RenderContext.contractOverviewAccident(contract: Contract) {
    detailsGridEntry("Versicherte Personen", contract.additionalLine3.replace("VP:", ""))
}

/**
 * Anzeige von Leben - Informationen
 *
 * @param contract beinhaltet die Vertragsinformationen
 */
private fun RenderContext.contractOverviewLife(contract: Contract) {
    detailsGridEntry("Versicherte Personen", buildString {
        append(contract.additionalLine2.replace("VP:", ""))
        if (contract.additionalLine3.isNotEmpty()) {
            append(". ${contract.additionalLine3}")
        }
    })
}